import {
  BayLevelEnum,
  ForeAftEnum,
  IBayLevelOffsetBottomBase,
} from "open-vessel-definition";
import {
  EInvalidContainerReasonEnum,
  IContainerInformation,
  IContainerInformationExtended,
} from "../../types/IContainerInformation";
import {
  IBayPattern,
  IPositionPattern,
  IRowPattern,
  ISlotPattern,
  ITierPattern,
} from "../../types/IPositionPatterns";
import {
  cloneObject,
  feetToMillimeters,
  pad3,
} from "@tedivo/tedivo-pure-helpers";

import { BayLcgVcgTcgAndPairings } from "../BayLcgVcgTcgAndPairings";
import { ICellCGsInfo } from "../CellInfo";
import { IGetAvgCgsOutput } from "../BaySizesAndCgs";
import { calculateGeneralMedianVcg } from "./calculateMedianVcg";
import { createRowsFromConfig } from "../../createRowsFromConfig";

export function getContainersCgs(
  bayLcgVcgTcgAndPairings: BayLcgVcgTcgAndPairings,
  containers: IContainerInformation[],
  options?: { verticalSeparation?: number },
  offsetBottomBases?: Array<IBayLevelOffsetBottomBase>,
): TGetContainersCgsResult {
  // Get info from the bayLcgVcgTcgAndPairings
  const {
    pairedBaysCalc: { pairedBays, unpairedBays },
    sizeSummary: { centerLineRow, maxRow },
    bayLevelPositionsDict,
    bayLevelPositionsAbove,
    bayLevelPositionsBelow,
    missingImportantLcgs,
    missingImportantVcgs,
    missingImportantTcgs,
    minTierAbove,
    lcgsBy20Bay,
  } = bayLcgVcgTcgAndPairings;

  const verticalSeparation = options?.verticalSeparation || 0;

  const everyRowInShip = createRowsFromConfig(!!centerLineRow, maxRow);
  const containersMissingCgsInfo: IContainerInformationExtended[] = [];
  const bayLevelCgsInfo: Record<IBayPattern, IBayCgsInfo> = {};
  const containerCgsByBayAndLevelAndRow: Record<
    TKeyPairedBaysLevelAndRow,
    ICellCGsInfo[]
  > = {};

  let maxHeightAbsolute = 0;
  let maxHeightWithSeparationAbsolute = 0;

  // 0. Make a dictionary of containers by position, and extend the info
  const containersDictByBay = containers
    .slice()
    .sort(sortByPositionAsc) // Sort by position ascending to avoid having to sort later (Bay-Row-Tier)
    .reduce(createDictionaryByPositionAndExtendInfo, {});

  const containersCgsArray: ICellCGsInfo[] = [];
  const containersCgsByPk: Record<string, ICellCGsInfo> = {};

  // 1. Starting from pairedBaysCalc, iterate the bays, paired first
  pairedBays.forEach((pairedBay) => {
    const { baysPrefixes, level } = pairedBay;

    if (level === BayLevelEnum.TWINDECK) return;

    const containersInTheseBays = Array.from(baysPrefixes)
      .flatMap((baysPrefix) => containersDictByBay[baysPrefix] || [])
      .filter((cont) => cont.level === level);

    let bayHeight = 0;
    let portTcg = 0;
    let stbdTcg = 0;
    let bottomBase = Infinity;
    let lowestTier: ITierPattern = "00";

    // 2. For each row, iterate the containers, stack up the containers
    everyRowInShip.forEach((row) => {
      const contsInRow = containersInTheseBays?.filter(
        (cont) => cont.row === row,
      );

      if (!contsInRow || contsInRow.length === 0) return;

      // Do we have 40ft containers in this row?
      const tiersWith40ContainersInThisRow = new Set(
        containersInTheseBays
          .filter((cont) => cont.size >= 40)
          .map((cont) => cont.tier),
      );

      let rowHeight = 0;

      // If no, stack up the 20ft containers, each bay separately
      if (tiersWith40ContainersInThisRow.size === 0) {
        Array.from(baysPrefixes).forEach((bay) => {
          // Stack up the 20ft containers
          const {
            cgs: contCgs,
            cgsByPk,
            stackHeight,
            stackHeightWithSeparation,
            lowestBottomBase: lowestBottomBaseOfBay,
            lowestTierOfRow,
            contsMissingCgsInfo,
            maxPortTcg,
            maxStbdTcg,
          } = stack20BayContainersOnly({
            bay,
            level,
            row,
            contsInRow,
          });

          if (lowestBottomBaseOfBay + stackHeight > maxHeightAbsolute)
            maxHeightAbsolute = lowestBottomBaseOfBay + stackHeight;

          if (
            lowestBottomBaseOfBay + stackHeightWithSeparation >
            maxHeightWithSeparationAbsolute
          )
            maxHeightWithSeparationAbsolute =
              lowestBottomBaseOfBay + stackHeightWithSeparation;

          if (lowestBottomBaseOfBay < bottomBase)
            bottomBase = lowestBottomBaseOfBay;

          if (Number(lowestTierOfRow) < Number(lowestTier))
            lowestTier = lowestTierOfRow;

          if (stackHeight > rowHeight) rowHeight = stackHeight;

          if (maxPortTcg > portTcg) portTcg = maxPortTcg;
          if (maxStbdTcg < stbdTcg) stbdTcg = maxStbdTcg;

          // Add the containers cgs to the array
          containersCgsArray.push(...contCgs);
          containersMissingCgsInfo.push(...contsMissingCgsInfo);
          Object.assign(containersCgsByPk, cgsByPk);
        });
      } else {
        // If yes, stack up the 20ft and 40ft containers, together
        const {
          cgs: contCgs,
          cgsByPk,
          stackHeight,
          stackHeightWithSeparation,
          lowestBottomBase: lowestBottomBaseOfBay,
          lowestTierOfRow,
          contsMissingCgsInfo,
          maxPortTcg,
          maxStbdTcg,
        } = stack20And40Containers(baysPrefixes, level, row, contsInRow);

        if (lowestBottomBaseOfBay + stackHeight > maxHeightAbsolute)
          maxHeightAbsolute = lowestBottomBaseOfBay + stackHeight;

        if (
          lowestBottomBaseOfBay + stackHeightWithSeparation >
          maxHeightWithSeparationAbsolute
        )
          maxHeightWithSeparationAbsolute =
            lowestBottomBaseOfBay + stackHeightWithSeparation;

        if (lowestBottomBaseOfBay < bottomBase)
          bottomBase = lowestBottomBaseOfBay;

        if (Number(lowestTierOfRow) < Number(lowestTier))
          lowestTier = lowestTierOfRow;

        if (stackHeight > rowHeight) rowHeight = stackHeight;

        if (maxPortTcg > portTcg) portTcg = maxPortTcg;
        if (maxStbdTcg < stbdTcg) stbdTcg = maxStbdTcg;

        // Add the containers cgs to the array
        containersCgsArray.push(...contCgs);
        containersMissingCgsInfo.push(...contsMissingCgsInfo);
        Object.assign(containersCgsByPk, cgsByPk);
      }

      if (rowHeight > bayHeight) bayHeight = rowHeight;
    });
  });

  // Do the same for the unpaired bays
  unpairedBays.forEach((unpairedBay) => {
    let portTcg = 0;
    let stbdTcg = 0;
    let bottomBase = Infinity;
    let lowestTier: ITierPattern = "00";

    const { base: bay, level } = unpairedBay;

    const containersInTheseBays = containersDictByBay[bay]?.filter(
      (cont) => cont.level === level,
    );

    // 2. For each row, iterate the containers
    everyRowInShip.forEach((row) => {
      const contsInRow = containersInTheseBays?.filter(
        (cont) => cont.row === row,
      );

      if (!contsInRow || contsInRow.length === 0) return;

      // Stack up the containers
      const {
        cgs: contCgs,
        cgsByPk,
        stackHeight,
        stackHeightWithSeparation,
        lowestBottomBase: lowestBottomBaseOfBay,
        lowestTierOfRow,
        contsMissingCgsInfo,
        maxPortTcg,
        maxStbdTcg,
      } = stack20BayContainersOnly({
        bay,
        level,
        row,
        contsInRow,
      });

      if (lowestBottomBaseOfBay + stackHeight > maxHeightAbsolute)
        maxHeightAbsolute = lowestBottomBaseOfBay + stackHeight;

      if (
        lowestBottomBaseOfBay + stackHeightWithSeparation >
        maxHeightWithSeparationAbsolute
      )
        maxHeightWithSeparationAbsolute =
          lowestBottomBaseOfBay + stackHeightWithSeparation;

      if (lowestBottomBaseOfBay < bottomBase)
        bottomBase = lowestBottomBaseOfBay;

      if (Number(lowestTierOfRow) < Number(lowestTier))
        lowestTier = lowestTierOfRow;

      if (maxPortTcg > portTcg) portTcg = maxPortTcg;
      if (maxStbdTcg < stbdTcg) stbdTcg = maxStbdTcg;

      // Add the containers cgs to the array
      containersCgsArray.push(...contCgs);
      containersMissingCgsInfo.push(...contsMissingCgsInfo);
      Object.assign(containersCgsByPk, cgsByPk);
    });
  });

  const { generalMedianVcg, midVcgByBay, offsetBottomBasesDict } =
    calculateGeneralMedianVcg(
      bayLevelCgsInfo,
      missingImportantVcgs,
      offsetBottomBases || [],
    );

  return {
    missingImportantLcgs,
    missingImportantVcgs,
    missingImportantTcgs,
    containersCgsArray,
    containersCgsByPk,
    containerCgsByBayAndLevelAndRow,
    bayLevelCgsInfo,
    maxHeightAbsolute,
    containersMissingCgsInfo,
    generalMedianVcg,
    midVcgByBay,
    offsetBottomBasesDict,
  };

  function createDictionaryByPositionAndExtendInfo(
    acc: Record<IBayPattern, IContainerInformationExtended[]>,
    contInfo: IContainerInformation,
  ) {
    const position = contInfo.position;
    const bay = position.substring(0, 3) as IBayPattern;
    const row = position.substring(3, 5) as IRowPattern;
    const tier = position.substring(5) as ITierPattern;
    const iBay = Number(bay);
    const level =
      Number(tier) >= minTierAbove ? BayLevelEnum.ABOVE : BayLevelEnum.BELOW;

    const contInfoExtended: IContainerInformationExtended = {
      ...contInfo,
      iBay,
      bay,
      level,
      row,
      tier,
    };

    if (!acc[bay]) acc[bay] = [];

    acc[bay].push(contInfoExtended);

    return acc;
  }

  function stack20BayContainersOnly({
    bay,
    level,
    row,
    contsInRow,
  }: {
    bay: IBayPattern;
    level: BayLevelEnum;
    row: IRowPattern;
    contsInRow: IContainerInformationExtended[];
  }): {
    cgs: ICellCGsInfo[];
    cgsByPk: Record<string, ICellCGsInfo>;
    stackHeight: number;
    stackHeightWithSeparation: number;
    lowestBottomBase: number;
    lowestTierOfRow: ITierPattern;
    maxPortTcg: number;
    maxStbdTcg: number;
    contsMissingCgsInfo: IContainerInformationExtended[];
  } {
    // Initialize variables to return
    const cgs: ICellCGsInfo[] = [];
    const cgsByPk: Record<string, ICellCGsInfo> = {};
    const contsMissingCgsInfo: IContainerInformationExtended[] = [];

    let stackHeight = 0;
    let stackHeightWithSeparation = 0;
    let maxPortTcg = 0;
    let maxStbdTcg = 0;

    // Select containers in row and sort from bottom -> top
    const contsInRowFromBottomToTop = contsInRow
      .filter((cont) => cont.bay === bay && cont.row === row)
      .sort(sortPositionByTierAsc);

    const lowestTierOfRow = contsInRowFromBottomToTop[0]?.tier;

    const blp = bayLevelPositionsDict[`${bay}-${level}`];

    if (!blp) {
      contsMissingCgsInfo.push(
        ...contsInRowFromBottomToTop.map((c) => ({
          ...c,
          invalidReason: EInvalidContainerReasonEnum.INEXISTENT_BAY,
        })),
      );

      return {
        cgs: [],
        cgsByPk: {},
        contsMissingCgsInfo,
        stackHeight: 0,
        stackHeightWithSeparation: 0,
        lowestBottomBase: 0,
        maxPortTcg: 0,
        maxStbdTcg: 0,
        lowestTierOfRow: lowestTierOfRow ?? "00",
      };
    }

    const countContainersMinusOne = contsInRowFromBottomToTop.length - 1;

    // Get the average CGs of the row, so we can interpolate the some values if missing
    const avgCGsOfRow = blp.getAvgCGs(row);

    // Find a valid TCG for the row. It should only be one.
    const tcg = missingImportantTcgs
      ? bayLcgVcgTcgAndPairings.getMissingTcg(row)
      : avgCGsOfRow?.avgTcg;

    const bottomBase = missingImportantVcgs
      ? bayLcgVcgTcgAndPairings.getMissingBottomBase(lowestTierOfRow)
      : blp.getAvgCGs(row)?.lowestBottomBase;

    if (tcg === undefined || bottomBase === undefined) {
      contsMissingCgsInfo.push(
        ...contsInRowFromBottomToTop.map((c) => ({
          ...c,
          invalidReason: EInvalidContainerReasonEnum.INEXISTENT_ROW,
        })),
      );

      return {
        cgs: [],
        cgsByPk: {},
        contsMissingCgsInfo,
        stackHeight: 0,
        stackHeightWithSeparation: 0,
        lowestBottomBase: 0,
        maxPortTcg: 0,
        maxStbdTcg: 0,
        lowestTierOfRow: lowestTierOfRow ?? "00",
      };
    }

    let lastTierStacked: ITierPattern | undefined = undefined;

    // --> Main loop
    contsInRowFromBottomToTop.forEach((c, idxOfCont) => {
      // It shouldn't be the same tier as the last one (only one container per tier)
      if (lastTierStacked === c.tier) {
        contsMissingCgsInfo.push({
          ...c,
          invalidReason: EInvalidContainerReasonEnum.REPEATED_CELL,
        });
        return;
      }

      lastTierStacked = c.tier;
      const slotPos = `${c.row}${c.tier}` as ISlotPattern;
      const cellInfoBySize = blp.getCellInfo(slotPos)?.sizes?.get(c.size);

      let clonedCellInfoBySize: ICellCGsInfo | undefined =
        cloneObject(cellInfoBySize);

      // If not found, create a cell info from the average CGs of the row
      if (clonedCellInfoBySize === undefined) {
        clonedCellInfoBySize = createCellInfoFromAvgCGs(c, avgCGsOfRow);
      }

      // If no cell info by size, the container is not in the bay
      if (!clonedCellInfoBySize) {
        contsMissingCgsInfo.push({
          ...c,
          invalidReason: EInvalidContainerReasonEnum.INVALID_POSITION,
        });

        return;
      }

      clonedCellInfoBySize.meta = c;

      if (missingImportantLcgs) {
        clonedCellInfoBySize.lcg = bayLcgVcgTcgAndPairings.getMissing20Lcg(
          blp.isoBay,
          c.size,
        );

        if (clonedCellInfoBySize.lcg === undefined) {
          const lcgT = bayLcgVcgTcgAndPairings.getMissing20Lcg(blp.isoBay, 20);

          if (lcgT !== undefined && clonedCellInfoBySize.extending) {
            const difSizeMiddle = feetToMillimeters((c.size - 20) * 0.5);
            clonedCellInfoBySize.lcg =
              clonedCellInfoBySize.extending === ForeAftEnum.FWD
                ? lcgT + difSizeMiddle
                : lcgT - difSizeMiddle;
          }
        }
      }

      clonedCellInfoBySize.bottomBase = bottomBase;
      clonedCellInfoBySize.tcg = tcg;

      if (clonedCellInfoBySize.bottomBase === undefined) {
        contsMissingCgsInfo.push({
          ...c,
          invalidReason: EInvalidContainerReasonEnum.INEXISTENT_TIER,
        });

        return; // Cannot continue calculations
      }

      if (clonedCellInfoBySize.tcg !== undefined) {
        if (maxPortTcg > clonedCellInfoBySize.tcg)
          maxPortTcg = clonedCellInfoBySize.tcg;
        if (maxStbdTcg < clonedCellInfoBySize.tcg)
          maxStbdTcg = clonedCellInfoBySize.tcg;
      }

      // Set the bottom base of the container (before adding the height and separation)
      clonedCellInfoBySize.tierBottomBase =
        clonedCellInfoBySize.bottomBase + stackHeightWithSeparation;

      // Now update heights:
      // Add the container height to the stack height
      stackHeight += c.height;
      // Add the container height and the vertical separation to the stack height with separation
      stackHeightWithSeparation +=
        c.height +
        (idxOfCont === countContainersMinusOne ? 0 : verticalSeparation);

      // Finally, add the container's cgs to the array
      cgs.push(clonedCellInfoBySize);
      cgsByPk[c.pk] = clonedCellInfoBySize;

      // Save the container's cgs to the dictionary by bay, level and row
      const key = `${bay}-${level}-${c.row}` as TKeyPairedBaysLevelAndRow;
      if (!containerCgsByBayAndLevelAndRow[key])
        containerCgsByBayAndLevelAndRow[key] = [];

      containerCgsByBayAndLevelAndRow[key].push(clonedCellInfoBySize);
    });

    updateBayLevelCgsInfo({
      oddBaysToDefine: [bay],
      level: level as BayLevelEnum.ABOVE | BayLevelEnum.BELOW,
      portTcg: maxPortTcg,
      stbdTcg: maxStbdTcg,
      bottomBase: blp.minBottomBase ?? 0,
      bottomTier: lowestTierOfRow,
      currentMaxHeight: stackHeightWithSeparation,
      cont: contsInRow[0],
    });

    return {
      cgs,
      cgsByPk,
      contsMissingCgsInfo,
      stackHeight,
      stackHeightWithSeparation,
      lowestBottomBase: blp.minBottomBase ?? 0,
      lowestTierOfRow,
      maxPortTcg,
      maxStbdTcg,
    };
  }

  function stack20And40Containers(
    bays: Set<IBayPattern>,
    level: BayLevelEnum,
    row: IRowPattern,
    contsInRow: IContainerInformationExtended[],
  ): {
    cgs: ICellCGsInfo[];
    cgsByPk: Record<string, ICellCGsInfo>;
    stackHeight: number;
    stackHeightWithSeparation: number;
    lowestBottomBase: number;
    lowestTierOfRow: ITierPattern;
    maxPortTcg: number;
    maxStbdTcg: number;
    contsMissingCgsInfo: IContainerInformationExtended[];
  } {
    // Initialize variables to return
    const cgs: ICellCGsInfo[] = [];
    const cgsByPk: Record<string, ICellCGsInfo> = {};
    const contsMissingCgsInfo: IContainerInformationExtended[] = [];

    let stackHeightFwd = 0;
    let stackHeightWithSeparationFwd = 0;
    let stackHeightAft = 0;
    let stackHeightWithSeparationAft = 0;
    let maxPortTcg = 0;
    let maxStbdTcg = 0;

    // Select containers in row and sort from bottom -> top
    const contsInRowFromBottomToTop = contsInRow
      .filter((cont) => bays.has(cont.bay) && cont.row === row)
      .sort(sortPositionByTierAsc);

    const lowestTierOfRow = contsInRowFromBottomToTop[0]?.tier;

    const blps = (
      level === BayLevelEnum.ABOVE
        ? bayLevelPositionsAbove
        : bayLevelPositionsBelow
    ).filter((blp) => bays.has(blp.isoBay));

    // --> Fast return
    if (blps.length === 0) {
      contsMissingCgsInfo.push(
        ...contsInRowFromBottomToTop.map((c) => ({
          ...c,
          invalidReason: EInvalidContainerReasonEnum.INEXISTENT_BAY,
        })),
      );

      return {
        cgs: [],
        cgsByPk: {},
        contsMissingCgsInfo,
        stackHeight: 0,
        stackHeightWithSeparation: 0,
        lowestBottomBase: 0,
        lowestTierOfRow: "00",
        maxPortTcg: 0,
        maxStbdTcg: 0,
      };
    }

    const bayWhere40sAreDefined = blps.find((blp) => blp.maxSize >= 40);

    // Get the average CGs of the row, so we can interpolate the some values if missing
    const avgCGsOfRow = bayWhere40sAreDefined?.getAvgCGs(row);

    // Find a valid TCG for the row. It should only be one.
    const tcg = missingImportantTcgs
      ? bayLcgVcgTcgAndPairings.getMissingTcg(row)
      : avgCGsOfRow?.avgTcg;

    const bottomBase = missingImportantVcgs
      ? bayLcgVcgTcgAndPairings.getMissingBottomBase(lowestTierOfRow)
      : blps.reduce(
          (acc, blp) =>
            Math.min(acc, blp.getAvgCGs(row)?.lowestBottomBase ?? Infinity),
          Infinity,
        );

    if (tcg === undefined || bottomBase === undefined) {
      contsMissingCgsInfo.push(
        ...contsInRowFromBottomToTop.map((c) => ({
          ...c,
          invalidReason: EInvalidContainerReasonEnum.INEXISTENT_ROW,
        })),
      );

      return {
        cgs: [],
        cgsByPk: {},
        contsMissingCgsInfo,
        stackHeight: 0,
        stackHeightWithSeparation: 0,
        lowestBottomBase: 0,
        lowestTierOfRow: "00",
        maxPortTcg: 0,
        maxStbdTcg: 0,
      };
    }

    /** This is a map of the tiers that have been stacked, to avoid having the same tier stacked twice or a 20 and a 40 */
    const stackedMap: Record<
      ITierPattern,
      { fwd20?: number; aft20?: number; paired40?: number }
    > = {};

    const isoBayFwd20 = blps[0].isoBay;
    const isoBayAft20 = blps[1].isoBay;
    const isoBayPaired40 = bayWhere40sAreDefined?.isoBay;

    // --> Main loop
    contsInRowFromBottomToTop.forEach((c) => {
      const tier = c.tier;

      if (!stackedMap[tier]) {
        // Initialize the map for the tier
        stackedMap[tier] = { fwd20: 0, aft20: 0, paired40: 0 };
      }

      let propToFill: "fwd20" | "aft20" | "paired40" | undefined = undefined;
      let bayWhereThisSlotIsDefined: IBayPattern | undefined = undefined;

      if (c.size < 40) {
        if (c.bay === isoBayFwd20) {
          bayWhereThisSlotIsDefined = isoBayFwd20;
          propToFill = "fwd20";
        } else if (c.bay === isoBayAft20) {
          bayWhereThisSlotIsDefined = isoBayAft20;
          propToFill = "aft20";
        } else {
          contsMissingCgsInfo.push({
            ...c,
            invalidReason: EInvalidContainerReasonEnum.INVALID_POSITION,
          });
          return;
        }
      } else {
        bayWhereThisSlotIsDefined = isoBayPaired40;
        propToFill = "paired40";
      }

      if (!bayWhereThisSlotIsDefined) {
        contsMissingCgsInfo.push({
          ...c,
          invalidReason: EInvalidContainerReasonEnum.INVALID_POSITION,
        });
        return;
      }

      // If the tier has stacked two 20s or a 40 or the same position, the container is invalid
      if (
        (stackedMap[tier].fwd20 && stackedMap[tier].aft20) || // Already stacked two 20s
        stackedMap[tier].paired40 || // Already stacked a 40
        stackedMap[tier][propToFill] // Already stacked the same position
      ) {
        contsMissingCgsInfo.push({
          ...c,
          invalidReason: EInvalidContainerReasonEnum.REPEATED_CELL,
        });
        return;
      }

      // Set the "slot" property to true
      stackedMap[tier][propToFill] = c.height;

      const slotPos = `${c.row}${c.tier}` as ISlotPattern;

      const cellInfoBySize = bayLevelPositionsDict[
        `${bayWhereThisSlotIsDefined}-${level}`
      ]
        .getCellInfo(slotPos)
        ?.sizes?.get(c.size);

      let clonedCellInfoBySize: ICellCGsInfo | undefined =
        cloneObject(cellInfoBySize);

      // If not found, create a cell info from the average CGs of the row
      if (clonedCellInfoBySize === undefined) {
        clonedCellInfoBySize = createCellInfoFromAvgCGs(c, avgCGsOfRow);
      }

      // If no cell info by size, the container is not in the bay
      if (!clonedCellInfoBySize) {
        contsMissingCgsInfo.push({
          ...c,
          invalidReason: EInvalidContainerReasonEnum.INVALID_POSITION,
        });

        return;
      }

      clonedCellInfoBySize.meta = c;
      clonedCellInfoBySize.bottomBase = bottomBase;
      clonedCellInfoBySize.tcg = tcg;

      if (missingImportantLcgs) {
        clonedCellInfoBySize.lcg = bayLcgVcgTcgAndPairings.getMissing20Lcg(
          bayWhereThisSlotIsDefined,
          c.size,
        );

        if (clonedCellInfoBySize.lcg === undefined) {
          const lcgT = bayLcgVcgTcgAndPairings.getMissing20Lcg(
            bayWhereThisSlotIsDefined,
            20,
          );

          if (lcgT !== undefined && clonedCellInfoBySize.extending) {
            const difSizeMiddle = feetToMillimeters((c.size - 20) * 0.5);
            clonedCellInfoBySize.lcg =
              clonedCellInfoBySize.extending === ForeAftEnum.FWD
                ? lcgT + difSizeMiddle
                : lcgT - difSizeMiddle;
          }
        }
      }

      if (maxPortTcg > clonedCellInfoBySize.tcg)
        maxPortTcg = clonedCellInfoBySize.tcg;
      if (maxStbdTcg < clonedCellInfoBySize.tcg)
        maxStbdTcg = clonedCellInfoBySize.tcg;

      if (propToFill === "fwd20") {
        clonedCellInfoBySize.tierBottomBase =
          bottomBase + stackHeightWithSeparationFwd;
      } else if (propToFill === "aft20") {
        clonedCellInfoBySize.tierBottomBase =
          bottomBase + stackHeightWithSeparationAft;
      } else if (propToFill === "paired40") {
        clonedCellInfoBySize.tierBottomBase =
          bottomBase +
          Math.max(stackHeightWithSeparationFwd, stackHeightWithSeparationAft);
      }

      if (propToFill === "fwd20") {
        stackHeightFwd += c.height;
        stackHeightWithSeparationFwd += c.height + verticalSeparation;
      } else if (propToFill === "aft20") {
        stackHeightAft += c.height;
        stackHeightWithSeparationAft += c.height + verticalSeparation;
      } else if (propToFill === "paired40") {
        stackHeightFwd += c.height;
        stackHeightWithSeparationFwd += c.height + verticalSeparation;
        stackHeightAft += c.height;
        stackHeightWithSeparationAft += c.height + verticalSeparation;
      }

      // Finally, add the container's cgs to the array
      cgs.push(clonedCellInfoBySize);
      cgsByPk[c.pk] = clonedCellInfoBySize;

      // Save the container's cgs to the dictionary by bay, level and row
      const keys = (
        [
          propToFill === "fwd20" ? isoBayFwd20 : undefined,
          propToFill === "aft20" ? isoBayAft20 : undefined,
          propToFill === "paired40" ? isoBayPaired40 : undefined,
        ].filter(Boolean) as IBayPattern[]
      ).map((bay) => `${bay}-${level}-${c.row}` as TKeyPairedBaysLevelAndRow);

      keys.forEach((key) => {
        if (!containerCgsByBayAndLevelAndRow[key])
          containerCgsByBayAndLevelAndRow[key] = [];

        containerCgsByBayAndLevelAndRow[key].push(clonedCellInfoBySize);
      });

      updateBayLevelCgsInfo({
        oddBaysToDefine: [isoBayFwd20, isoBayAft20],
        level: level as BayLevelEnum.ABOVE | BayLevelEnum.BELOW,
        portTcg: maxPortTcg,
        stbdTcg: maxStbdTcg,
        bottomBase: bottomBase ?? 0,
        bottomTier: lowestTierOfRow,
        currentMaxHeight: Math.max(
          stackHeightWithSeparationFwd,
          stackHeightWithSeparationAft,
        ),
        cont: c,
      });
    });

    return {
      cgs,
      cgsByPk,
      contsMissingCgsInfo,
      stackHeight: Math.max(stackHeightFwd, stackHeightAft),
      stackHeightWithSeparation:
        Math.max(stackHeightWithSeparationFwd, stackHeightWithSeparationAft) -
        verticalSeparation,
      lowestBottomBase: bottomBase,
      lowestTierOfRow,
      maxPortTcg,
      maxStbdTcg,
    };
  }

  function updateBayLevelCgsInfo({
    oddBaysToDefine,
    level,
    portTcg,
    stbdTcg,
    bottomBase,
    bottomTier,
    currentMaxHeight,
    cont,
  }: {
    oddBaysToDefine: IBayPattern[];
    level: BayLevelEnum.ABOVE | BayLevelEnum.BELOW;
    portTcg: number;
    stbdTcg: number;
    bottomBase: number;
    bottomTier: ITierPattern;
    currentMaxHeight: number;
    cont: IContainerInformationExtended;
  }) {
    // Save the bay level vcgs info
    oddBaysToDefine.forEach((oBay) => {
      if (!bayLevelCgsInfo[oBay]) bayLevelCgsInfo[oBay] = {};
      const bayLevelVcgsInfoOfBay = bayLevelCgsInfo[oBay];

      if (!bayLevelVcgsInfoOfBay[level]) {
        const positions = new Set<IPositionPattern>();
        positions.add(cont.position);

        bayLevelVcgsInfoOfBay[level] = {
          bottomBase,
          bottomTier,
          maxHeightOfBay: currentMaxHeight || 0,
          maxHeightWithSeparationOfBay: currentMaxHeight || 0,
          maxHeightAbsoluteOfBay: bottomBase + currentMaxHeight || 0,
          maxHeightWithSeparationAbsoluteOfBay:
            bottomBase + currentMaxHeight || 0,
          portTcg,
          starboardTcg: stbdTcg,
          maxForeLcg: lcgsBy20Bay[oBay]?.maxForeLcg || 0,
          minAftLcg: lcgsBy20Bay[oBay]?.minAftLcg || 0,
          positions,
        };
      } else {
        bayLevelVcgsInfoOfBay[level].positions.add(cont.position);

        if (bayLevelVcgsInfoOfBay[level].portTcg > portTcg)
          bayLevelVcgsInfoOfBay[level].portTcg = portTcg;

        if (bayLevelVcgsInfoOfBay[level].starboardTcg < stbdTcg)
          bayLevelVcgsInfoOfBay[level].starboardTcg = stbdTcg;

        if (bayLevelVcgsInfoOfBay[level].bottomBase > bottomBase)
          bayLevelVcgsInfoOfBay[level].bottomBase = bottomBase;

        if (bayLevelVcgsInfoOfBay[level].bottomTier > bottomTier)
          bayLevelVcgsInfoOfBay[level].bottomTier = bottomTier;

        if (bayLevelVcgsInfoOfBay[level].maxHeightOfBay < currentMaxHeight)
          bayLevelVcgsInfoOfBay[level].maxHeightOfBay = currentMaxHeight;

        if (
          bayLevelVcgsInfoOfBay[level].maxHeightWithSeparationOfBay <
          currentMaxHeight
        )
          bayLevelVcgsInfoOfBay[level].maxHeightWithSeparationOfBay =
            currentMaxHeight;

        if (
          bayLevelVcgsInfoOfBay[level].maxHeightAbsoluteOfBay <
          bottomBase + currentMaxHeight
        )
          bayLevelVcgsInfoOfBay[level].maxHeightAbsoluteOfBay =
            bottomBase + currentMaxHeight;

        if (
          bayLevelVcgsInfoOfBay[level].maxHeightWithSeparationAbsoluteOfBay <
          bottomBase + currentMaxHeight
        )
          bayLevelVcgsInfoOfBay[level].maxHeightWithSeparationAbsoluteOfBay =
            bottomBase + currentMaxHeight;
      }
    });
  }
}

function createCellInfoFromAvgCGs(
  c: IContainerInformationExtended,
  avgCGsOfRow: IGetAvgCgsOutput | undefined,
): ICellCGsInfo | undefined {
  if (!avgCGsOfRow) return undefined;

  const cellInfoBySize = avgCGsOfRow.perSize.get(c.size);

  if (cellInfoBySize) {
    const clonedCellInfoBySize = cloneObject(cellInfoBySize);
    return clonedCellInfoBySize;
  } else {
    return {
      ...c,
      size: c.size,
      isoOddBay: avgCGsOfRow.isoOddBay,
      lcg: c.size < 40 ? avgCGsOfRow.avgLcg20 : avgCGsOfRow.avgLcg40,
      tcg: avgCGsOfRow.avgTcg,
      bottomBase: avgCGsOfRow.lowestBottomBase,
      bottomTier: avgCGsOfRow.lowestBottomTier,
    };
  }
}

export function getContainersCgsOld(
  bayLcgVcgTcgAndPairings: BayLcgVcgTcgAndPairings,
  containers: IContainerInformation[],
  options?: { verticalSeparation?: number },
): TGetContainersCgsResult {
  const {
    minTierAbove,
    bayLevelPositionsDict,
    lcgsBy20Bay,
    missingImportantLcgs,
    missingImportantVcgs,
    missingImportantTcgs,
    sizeSummary: { centerLineRow, maxRow },
    pairedBaysCalc: { pairedBaysPer20Bay },
  } = bayLcgVcgTcgAndPairings;

  const containersCgsByPosition: Record<string, ICellCGsInfo> = {};

  const bayLevelCgsInfo: Record<IBayPattern, IBayCgsInfo> = {};

  const containersCgsArray: ICellCGsInfo[] = [];

  const containerCgsByPairedBaysAndLevelAndRow: Record<
    TKeyPairedBaysLevelAndRow,
    ICellCGsInfo[]
  > = {};

  const contPositionsPushed = new Set<IPositionPattern>();

  const containersDictByBayAndLevelAndRow = containers
    .slice()
    .sort(sortByPositionAsc) // Sort by position ascending to avoid having to sort later (Bay-Row-Tier)
    .reduce(createDictionaryPerBayLevelAndRow, {});

  const baysAlreadyCalculated = new Set<`${IBayPattern}-${BayLevelEnum}`>();
  const allRowsInShip = createRowsFromConfig(!!centerLineRow, maxRow);
  const all20BaysWithLevel = Object.keys(
    pairedBaysPer20Bay,
  ) as `${IBayPattern}-${BayLevelEnum.ABOVE | BayLevelEnum.BELOW}`[];

  let shipMaxHeight = 0;

  all20BaysWithLevel.forEach((bayAndLevel) => {
    if (baysAlreadyCalculated.has(bayAndLevel)) return;

    const [bay, levelStr] = bayAndLevel.split("-") as [IBayPattern, string];

    const level =
      levelStr === "1"
        ? BayLevelEnum.ABOVE
        : levelStr === "2"
        ? BayLevelEnum.BELOW
        : undefined;

    if (!level) return;

    const pairedBay = pairedBaysPer20Bay[bayAndLevel];

    // baysAlreadyCalculated.add(bayAndLevel);
    // if (pairedBay) baysAlreadyCalculated.add(`${pairedBay}-${level}`);

    let maxHeight = 0;
    let portTcg = 0;
    let stbdTcg = 0;

    // Iterate over every row in the bay (up to the max row of the ship)
    allRowsInShip.forEach((row) => {
      const containersAtPairedRow = [
        ...(containersDictByBayAndLevelAndRow[`${bay}-${level}-${row}`] || []),
        // ...(pairedBay
        //   ? containersDictByBayAndLevelAndRow[`${pairedBay}-${level}-${row}`] ||
        //     []
        //   : []),
      ];

      if (containersAtPairedRow.length === 0) return;

      // Sort containers in row from bottom -> top
      const contsInRowFromBottomToTop = containersAtPairedRow.sort(
        sortPositionByTierAsc,
      );

      const lowestTierOfRow = contsInRowFromBottomToTop[0]?.tier;

      const oddBaysToDefine: Set<IBayPattern> = new Set();

      // Iterate to add up (stack) the containers in the same row
      contsInRowFromBottomToTop.forEach((c) => {
        const slotPos = `${c.row}${c.tier}` as ISlotPattern;
        const iBay = c.iBay;

        // Look for the 20 in the BLP, or the 40 in the contiguous BLPs (-1 or +1)
        const isLessThan40ft = c.size < 40;
        let cellInfoBySize: ICellCGsInfo | undefined;

        if (isLessThan40ft) {
          // Look up in the same bay definition
          const blp = bayLevelPositionsDict[`${c.bay}-${level}`];
          cellInfoBySize = blp?.getCellInfo(slotPos)?.sizes?.get(c.size);

          if (cellInfoBySize) {
            oddBaysToDefine.add(c.bay);
            if (blp && portTcg > blp.minTcg) portTcg = blp.minTcg;
            if (blp && stbdTcg < blp.maxTcg) stbdTcg = blp.maxTcg;
          }
        } else {
          if (iBay % 2 === 0) {
            // Look up in the contiguous bays definitions
            const blpFwd = bayLevelPositionsDict[`${pad3(iBay - 1)}-${level}`];
            const blpAft = bayLevelPositionsDict[`${pad3(iBay + 1)}-${level}`];

            // Look FWD
            cellInfoBySize =
              blpFwd?.pairedBay === ForeAftEnum.AFT
                ? blpFwd?.getCellInfo(slotPos)?.sizes?.get(c.size)
                : undefined;

            // No? Look AFT
            if (!cellInfoBySize) {
              cellInfoBySize =
                blpAft?.pairedBay === ForeAftEnum.FWD
                  ? blpAft?.getCellInfo(slotPos)?.sizes?.get(c.size)
                  : undefined;
            }

            if (blpFwd && cellInfoBySize) {
              oddBaysToDefine.add(pad3(iBay - 1));
              if (portTcg > blpFwd.minTcg) portTcg = blpFwd.minTcg;
              if (stbdTcg < blpFwd.maxTcg) stbdTcg = blpFwd.maxTcg;
            }

            if (blpAft && cellInfoBySize) {
              oddBaysToDefine.add(pad3(iBay + 1));
              if (portTcg > blpAft.minTcg) portTcg = blpAft.minTcg;
              if (stbdTcg < blpAft.maxTcg) stbdTcg = blpAft.maxTcg;
            }
          } else {
            const blpA = bayLevelPositionsDict[`${c.bay}-${level}`];
            const blpB =
              blpA.pairedBay === ForeAftEnum.FWD
                ? bayLevelPositionsDict[`${pad3(iBay - 2)}-${level}`]
                : blpA.pairedBay === ForeAftEnum.AFT
                ? bayLevelPositionsDict[`${pad3(iBay + 2)}-${level}`]
                : undefined;

            // Look Self
            cellInfoBySize =
              blpA?.pairedBay === ForeAftEnum.AFT
                ? blpA?.getCellInfo(slotPos)?.sizes?.get(c.size)
                : undefined;

            // No? Look Paired
            if (!cellInfoBySize && blpB) {
              cellInfoBySize =
                blpB?.pairedBay === ForeAftEnum.FWD
                  ? blpB?.getCellInfo(slotPos)?.sizes?.get(c.size)
                  : undefined;
            }

            if (blpA && cellInfoBySize) {
              oddBaysToDefine.add(blpA.isoBay);
              if (portTcg > blpA.minTcg) portTcg = blpA.minTcg;
              if (stbdTcg < blpA.maxTcg) stbdTcg = blpA.maxTcg;
            }

            if (blpB && cellInfoBySize) {
              oddBaysToDefine.add(blpB.isoBay);
              if (portTcg > blpB.minTcg) portTcg = blpB.minTcg;
              if (stbdTcg < blpB.maxTcg) stbdTcg = blpB.maxTcg;
            }
          }
        }

        if (!cellInfoBySize) {
          return; // Cannot continue calculations
        }

        const clonedCellInfoBySize = cloneObject(cellInfoBySize);
        clonedCellInfoBySize.meta = cloneObject(c);

        if (missingImportantLcgs) {
          clonedCellInfoBySize.lcg = bayLcgVcgTcgAndPairings.getMissing20Lcg(
            cellInfoBySize.isoOddBay,
            c.size,
          );

          if (clonedCellInfoBySize.lcg === undefined) {
            const lcgT = bayLcgVcgTcgAndPairings.getMissing20Lcg(
              cellInfoBySize.isoOddBay,
              20,
            );

            if (lcgT !== undefined && clonedCellInfoBySize.extending) {
              const difSizeMiddle = feetToMillimeters((c.size - 20) * 0.5);
              clonedCellInfoBySize.lcg =
                clonedCellInfoBySize.extending === ForeAftEnum.FWD
                  ? lcgT + difSizeMiddle
                  : lcgT - difSizeMiddle;
            }
          }
        }

        if (missingImportantVcgs) {
          clonedCellInfoBySize.bottomBase =
            bayLcgVcgTcgAndPairings.getMissingBottomBase(lowestTierOfRow);
        }

        if (missingImportantTcgs) {
          clonedCellInfoBySize.tcg = bayLcgVcgTcgAndPairings.getMissingTcg(
            c.row,
          );
        }

        if (clonedCellInfoBySize.bottomBase === undefined) {
          return; // Cannot continue calculations
        }

        if (clonedCellInfoBySize.tcg !== undefined) {
          if (portTcg > clonedCellInfoBySize.tcg)
            portTcg = clonedCellInfoBySize.tcg;
          if (stbdTcg < clonedCellInfoBySize.tcg)
            stbdTcg = clonedCellInfoBySize.tcg;
        }

        // Save the container info by position
        oddBaysToDefine.forEach((oBay) => {
          if (
            !containerCgsByPairedBaysAndLevelAndRow[`${oBay}-${level}-${row}`]
          )
            containerCgsByPairedBaysAndLevelAndRow[`${oBay}-${level}-${row}`] =
              [];

          containerCgsByPairedBaysAndLevelAndRow[
            `${oBay}-${level}-${row}`
          ].push(clonedCellInfoBySize);
        });

        containersCgsByPosition[c.position] = clonedCellInfoBySize;
        containersCgsArray.push(clonedCellInfoBySize);

        // Save the bay level vcgs info
        oddBaysToDefine.forEach((oBay) => {
          if (!bayLevelCgsInfo[oBay]) bayLevelCgsInfo[oBay] = {};
          const bayLevelVcgsInfoOfBay = bayLevelCgsInfo[oBay];

          if (!bayLevelVcgsInfoOfBay[level]) {
            const positions = new Set<IPositionPattern>();
            positions.add(c.position);

            bayLevelVcgsInfoOfBay[level] = {
              bottomBase: clonedCellInfoBySize.bottomBase || 0,
              bottomTier: c.tier,
              maxHeightOfBay: 0,
              maxHeightWithSeparationOfBay: 0,
              maxHeightAbsoluteOfBay: 0,
              maxHeightWithSeparationAbsoluteOfBay: 0,
              portTcg,
              starboardTcg: stbdTcg,
              maxForeLcg: lcgsBy20Bay[oBay]?.maxForeLcg || 0,
              minAftLcg: lcgsBy20Bay[oBay]?.minAftLcg || 0,
              positions,
            };
          } else {
            bayLevelVcgsInfoOfBay[level].positions.add(c.position);
            if (bayLevelVcgsInfoOfBay[level].portTcg > portTcg)
              bayLevelVcgsInfoOfBay[level].portTcg = portTcg;
            if (bayLevelVcgsInfoOfBay[level].starboardTcg < stbdTcg)
              bayLevelVcgsInfoOfBay[level].starboardTcg = stbdTcg;
          }
        });
      }); // End of contsInRowFromBottomToTop.forEach
    }); // End of rows allRowsInShip.forEach

    // 2nd pass to define the heights and bottom bases once all the containers are stacked
    const oddBaysToDefine = new Set([bay]);
    if (pairedBay) oddBaysToDefine.add(pairedBay);

    const { allContainersCgsOfRow, lowestBottomBaseOfRow } =
      getAllContainerCgsInTheRow(oddBaysToDefine, level);

    oddBaysToDefine.forEach((oBay) => {
      const bayLevelVcgsInfoOfBay = bayLevelCgsInfo[oBay];

      if (!bayLevelVcgsInfoOfBay?.[level]) return;

      let prevTier = allContainersCgsOfRow[0]?.meta?.tier;
      let addedUpHeight = 0;
      let lastHeight = 0;

      allContainersCgsOfRow.forEach((c) => {
        if (!c.meta) return;

        if (c.meta.tier !== prevTier) {
          addedUpHeight +=
            (c.meta.height || 0) + (options?.verticalSeparation || 0);

          prevTier = c.meta.tier;
        }

        c.tierBottomBase = addedUpHeight + lowestBottomBaseOfRow;
        lastHeight = c.meta.height || 0;
      });

      bayLevelVcgsInfoOfBay[level].bottomBase = lowestBottomBaseOfRow || 0;
      bayLevelVcgsInfoOfBay[level].maxHeightOfBay = addedUpHeight + lastHeight;

      if (bayLevelVcgsInfoOfBay[level].maxHeightOfBay > maxHeight)
        maxHeight = bayLevelVcgsInfoOfBay[level].maxHeightOfBay;

      if (shipMaxHeight < maxHeight + lowestBottomBaseOfRow)
        shipMaxHeight = maxHeight + lowestBottomBaseOfRow;
    });
  });

  return {
    missingImportantLcgs,
    missingImportantVcgs,
    missingImportantTcgs,
    containersCgsByPk: containersCgsByPosition || {},
    containersCgsArray,
    containerCgsByBayAndLevelAndRow: containerCgsByPairedBaysAndLevelAndRow,
    bayLevelCgsInfo,
    maxHeightAbsolute: shipMaxHeight,
    containersMissingCgsInfo: [],
    generalMedianVcg: 0,
    midVcgByBay: {},
    offsetBottomBasesDict: {},
  };

  /** Returns sets of CGs orderer from bottom to top, for every Bay and Row of the Bay level */
  function getAllContainerCgsInTheRow(
    oddBaysToDefine: Set<IBayPattern>,
    level: BayLevelEnum.ABOVE | BayLevelEnum.BELOW,
  ) {
    let lowestBottomBaseOfRow = Infinity;
    const allContainersCgsOfRow: Set<ICellCGsInfo> = new Set();

    oddBaysToDefine.forEach((bay) => {
      const bayLevelVcgsInfoOfBay = bayLevelCgsInfo[bay];
      if (!bayLevelVcgsInfoOfBay?.[level]) return;

      bayLevelVcgsInfoOfBay[level].positions.forEach((pos) => {
        const posCgs = containersCgsByPosition[pos];
        const row = posCgs?.meta?.row;

        if (
          posCgs === undefined ||
          row === undefined ||
          posCgs.bottomBase === undefined
        )
          return;

        allContainersCgsOfRow.add(posCgs);

        if (lowestBottomBaseOfRow > posCgs.bottomBase) {
          lowestBottomBaseOfRow = posCgs.bottomBase;
        }
      });
    });

    return {
      allContainersCgsOfRow: Array.from(allContainersCgsOfRow).sort(
        (a, b) => (Number(a.meta?.tier) || 0) - (Number(b.meta?.tier) || 0),
      ),
      lowestBottomBaseOfRow,
    };
  }

  /** Create a dictionary per bay, level and row, and add other properties.
   *
   *  Contains an array of the containers in that bay-level-row.
   *  Used to stack up containers heights */
  function createDictionaryPerBayLevelAndRow(
    acc: Record<TKeyPairedBaysLevelAndRow, IContainerInformationExtended[]>,
    contInfo: IContainerInformation,
  ) {
    const position = contInfo.position;
    const bay = position.substring(0, 3) as IBayPattern;
    const row = position.substring(3, 5) as IRowPattern;
    const tier = position.substring(5) as ITierPattern;
    const iBay = Number(bay);
    const level =
      Number(tier) >= minTierAbove ? BayLevelEnum.ABOVE : BayLevelEnum.BELOW;

    const positionKeys =
      contInfo.size < 40
        ? [
            // If it's on an odd bay we add it to one stack
            `${bay}-${level}-${row}`,
          ]
        : [
            // If it's on an even bay we add it to two stacks to find later in which it was declared
            `${pad3(iBay - 1)}-${level}-${row}`, // FWD declaration
            `${pad3(iBay + 1)}-${level}-${row}`, // AFT declaration
          ];

    (positionKeys as TKeyPairedBaysLevelAndRow[]).forEach((posKey) => {
      if (!acc[posKey]) acc[posKey] = [];
      const cBay = posKey.substring(0, 3) as IBayPattern;

      if (!contPositionsPushed.has(contInfo.position)) {
        acc[posKey].push({
          ...contInfo,
          iBay: Number(cBay),
          bay: cBay,
          row,
          tier,
          level,
        });

        contPositionsPushed.add(contInfo.position);
      }
    });

    return acc;
  }
}

/** String sort is enough because positions {BAY}{ROW}{TIER} share {BAY}{ROW} and the Tier (asc) is what we want */
function sortPositionByTierAsc(
  a: IContainerInformation,
  b: IContainerInformation,
) {
  const tierA = a.position.substring(5);
  const tierB = b.position.substring(5);
  return Number(tierA) - Number(tierB);
}

function sortByPositionAsc(a: IContainerInformation, b: IContainerInformation) {
  return a.position < b.position ? -1 : 1;
}

export interface IBayCgsInfo {
  [BayLevelEnum.ABOVE]?: IBayLevelCgsInfo;
  [BayLevelEnum.BELOW]?: IBayLevelCgsInfo;
}

interface IBayLevelCgsInfo {
  portTcg: number;
  starboardTcg: number;
  bottomBase: number;
  bottomTier: ITierPattern;
  maxHeightOfBay: number;
  maxHeightWithSeparationOfBay: number;
  maxHeightAbsoluteOfBay: number;
  maxHeightWithSeparationAbsoluteOfBay: number;
  maxForeLcg: number;
  minAftLcg: number;
  positions: Set<IPositionPattern>;
}

export type TGetContainersCgsResult = {
  missingImportantLcgs: boolean;
  missingImportantVcgs: boolean;
  missingImportantTcgs: boolean;

  containersCgsArray: ICellCGsInfo[];
  containersCgsByPk: Record<string, ICellCGsInfo>;
  containerCgsByBayAndLevelAndRow: Record<
    TKeyPairedBaysLevelAndRow,
    ICellCGsInfo[]
  >;

  containersMissingCgsInfo: IContainerInformationExtended[];

  generalMedianVcg: number;
  midVcgByBay: Record<IBayPattern, number>;
  offsetBottomBasesDict: Record<string, number>;

  bayLevelCgsInfo: Record<IBayPattern, IBayCgsInfo>;

  maxHeightAbsolute: number;
};

export type TKeyPairedBaysLevelAndRow =
  | `${IBayPattern}-${BayLevelEnum.ABOVE | BayLevelEnum.BELOW}-${IRowPattern}`
  | `${IBayPattern}|${IBayPattern}-${
      | BayLevelEnum.ABOVE
      | BayLevelEnum.BELOW}-${IRowPattern}`;
